"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLenis } from "lenis/react";
import { 
  X, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle,
  User,
  Phone,
  Mail,
  FileText,
  Send
} from "lucide-react";
import { applicantService } from "@/services/applicantService";
import toast from "react-hot-toast";
import { useLanguage } from "@/context/LanguageContext";

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: string;
  jobTitle?: string;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobTitle,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const lenis = useLenis();

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "unset";
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = "unset";
      lenis?.start();
    };
  }, [isOpen, lenis]);

  if (!isOpen || !mounted) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 15 * 1024 * 1024) {
        toast.error(t("careersPage.application.error.cvTooLarge") || "Dung lượng CV phải nhỏ hơn 15MB");
        return;
      }
      setCvFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Vui lòng nhập họ và tên");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Vui lòng nhập số điện thoại");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Vui lòng nhập email liên hệ");
      return;
    }
    if (!cvFile) {
      toast.error(t("careersPage.application.error.noCv") || "Vui lòng đính kèm CV của bạn");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Vui lòng nhập thư xin việc/lời nhắn");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const submitData = new FormData();
      submitData.append("applicantName", formData.name);
      submitData.append("phone", formData.phone);
      submitData.append("email", formData.email);
      submitData.append("message", formData.message);
      submitData.append("cv", cvFile);
      if (jobId) submitData.append("jobId", jobId);
      if (jobTitle) submitData.append("jobTitle", jobTitle);

      await applicantService.submitApplication(submitData);
      setIsSuccess(true);
      
      toast.custom((t_toast) => (
        <div className={`${t_toast.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#0a192f] shadow-lg rounded-2xl pointer-events-auto flex ring-1 ring-[#00f3ff]/50 border border-[#00f3ff]/20 z-[99999]`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <CheckCircle2 className="h-10 w-10 text-[#00f3ff] drop-shadow-[0_0_8px_#00f3ff]" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-black text-white uppercase tracking-wider">
                  {t("careersPage.application.success.title")}
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  {t("careersPage.application.success.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>
      ), { duration: 4000 });
      
      // Auto close after success
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({ name: "", phone: "", email: "", message: "" });
        setCvFile(null);
      }, 3000);
    } catch (err: any) {
      const msg =
        err?.originalError?.code === "ECONNABORTED"
          ? t("careersPage.application.error.slowNetwork") ||
            "Mạng chậm, tải CV quá lâu. Vui lòng thử lại hoặc dùng file nhỏ hơn."
          : err.message || "Gửi hồ sơ thất bại. Vui lòng thử lại sau.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto custom-scrollbar overscroll-contain" data-lenis-prevent>
      <div className="flex items-center justify-center min-h-full p-4 sm:p-6" data-lenis-prevent>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#040d21]/90 backdrop-blur-xl transition-opacity duration-500"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-3xl bg-[#0a192f]/40 backdrop-blur-2xl border border-[#00f3ff]/30 rounded-[3rem] shadow-[0_0_80px_rgba(0,243,255,0.2)] overflow-hidden animate-in fade-in zoom-in duration-500 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
        
        {/* Cyber Accents */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#00f3ff]/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#0066ff]/10 to-transparent pointer-events-none" />
        
        {/* Animated Scanning Line */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent opacity-20 animate-scan pointer-events-none" />

        {/* Glow Effects */}
        <div className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent shadow-[0_0_15px_#00f3ff]" />
        <div className="absolute bottom-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent shadow-[0_0_15px_#00f3ff]" />

        {/* Header */}
        <div className="px-6 sm:px-10 py-6 sm:py-8 border-b border-[#00f3ff]/10 flex items-center justify-between relative bg-[#00f3ff]/5 backdrop-blur-md sticky top-0 z-20">
          <div className="relative z-10 text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00f3ff]/10 border border-[#00f3ff]/20 text-[#00f3ff] text-[10px] font-black uppercase tracking-[0.2em] mb-3 shadow-[0_0_15px_rgba(0,243,255,0.1)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00f3ff] animate-pulse"></span>
              {t("careersPage.hero.badge") || "AHV CAREERS"}
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tighter leading-tight mb-2">
              {jobTitle ? (
                <>
                  {t("careersPage.application.title")}{" "}
                  <span className="text-[#00f3ff] drop-shadow-[0_0_12px_rgba(0,243,255,0.6)] italic">
                    {jobTitle}
                  </span>
                </>
              ) : (
                t("careersPage.application.defaultTitle")
              )}
            </h3>
            <p className="text-slate-400 text-xs sm:text-sm max-w-md font-medium leading-relaxed opacity-80">
              {t("careersPage.application.subtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-3 sm:p-4 bg-white/5 hover:bg-[#00f3ff]/20 rounded-[1.2rem] sm:rounded-[1.5rem] transition-all duration-500 text-slate-400 hover:text-[#00f3ff] group active:scale-90 border border-white/5 hover:border-[#00f3ff]/30 shadow-inner"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform duration-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-10 overflow-y-auto custom-scrollbar relative flex-1 overscroll-contain" data-lenis-prevent>
          {isSuccess ? (
            <div className="py-20 flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="w-24 h-24 bg-[#00f3ff]/10 rounded-[2rem] flex items-center justify-center mb-8 border border-[#00f3ff]/30 shadow-[0_0_50px_rgba(0,243,255,0.2)] rotate-12 transition-transform hover:rotate-0 duration-500">
                <CheckCircle2 className="w-10 h-10 text-[#00f3ff] drop-shadow-[0_0_8px_#00f3ff]" />
              </div>
              <h4 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter">
                {t("careersPage.application.success.title")}
              </h4>
              <p className="text-slate-400 max-w-xs mx-auto font-medium leading-relaxed">
                {t("careersPage.application.success.subtitle")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400 text-sm font-bold animate-shake">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-left">
                {/* Name */}
                <div className="space-y-3 group">
                  <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                    {t("careersPage.application.form.name")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                    <input
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("careersPage.application.form.placeholder.name")}
                      className="w-full pl-14 pr-6 py-4 sm:py-5 bg-white/5 border border-white/10 rounded-[1.2rem] sm:rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-3 group text-left">
                  <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                    {t("careersPage.application.form.phone")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t("careersPage.application.form.placeholder.phone")}
                      className="w-full pl-14 pr-6 py-4 sm:py-5 bg-white/5 border border-white/10 rounded-[1.2rem] sm:rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("careersPage.application.form.email")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("careersPage.application.form.placeholder.email")}
                    className="w-full pl-14 pr-6 py-4 sm:py-5 bg-white/5 border border-white/10 rounded-[1.2rem] sm:rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                  />
                </div>
              </div>

              {/* CV Upload */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("careersPage.application.form.cv")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <label className="block border-2 border-dashed border-white/10 rounded-[1.5rem] sm:rounded-[2rem] p-6 sm:p-10 text-center hover:bg-[#00f3ff]/5 hover:border-[#00f3ff]/40 transition-all cursor-pointer group/upload relative overflow-hidden bg-white/5">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00f3ff]/5 to-transparent opacity-0 group-hover/upload:opacity-100 transition-opacity" />
                  
                  <div className="relative z-10">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#00f3ff]/10 text-[#00f3ff] rounded-[1.2rem] sm:rounded-[1.5rem] flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover/upload:scale-110 group-hover/upload:rotate-3 transition-transform duration-500 border border-[#00f3ff]/20 shadow-[0_0_30px_rgba(0,243,255,0.1)]">
                      <UploadCloud className="w-8 h-8 sm:w-10 sm:h-10 drop-shadow-[0_0_8px_#00f3ff]" />
                    </div>
                    
                    <h4 className="text-white font-black text-base sm:text-lg mb-2 uppercase tracking-tight break-all px-2">
                      {cvFile ? cvFile.name : t("careersPage.application.form.placeholder.cv")}
                    </h4>
                    <p className="text-slate-500 text-xs sm:text-sm font-medium">
                      {cvFile 
                        ? `${t("careersPage.application.form.placeholder.cvSelected")}: ${(cvFile.size / (1024 * 1024)).toFixed(2)} MB` 
                        : t("careersPage.application.form.placeholder.cvSub")
                      }
                    </p>
                  </div>

                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* Message */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("careersPage.application.form.message")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-5 top-6 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                  <textarea
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("careersPage.application.form.placeholder.message")}
                    className="w-full pl-14 pr-6 py-4 sm:py-5 bg-white/5 border border-white/10 rounded-[1.2rem] sm:rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner resize-none overflow-hidden"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-end gap-4 sm:gap-6 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-10 py-5 font-black text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-white transition-colors"
                >
                  {t("careersPage.application.form.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-12 py-5 bg-gradient-to-r from-[#00f3ff] to-[#0066ff] text-[#040d21] font-black text-[10px] uppercase tracking-[0.3em] rounded-[1.2rem] sm:rounded-[1.5rem] shadow-[0_0_30px_rgba(0,243,255,0.4)] hover:shadow-[0_0_50px_rgba(0,243,255,0.6)] hover:-translate-y-1.5 active:translate-y-0 transition-all duration-500 flex items-center justify-center gap-4 disabled:opacity-50 disabled:translate-y-0 group overflow-hidden relative"
                >
                  <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-full transition-all duration-1000 ease-in-out pointer-events-none" />
                  
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-3 border-[#040d21]/30 border-t-[#040d21] rounded-full animate-spin" />
                      {t("careersPage.application.form.submitting")}
                    </>
                  ) : (
                    <>
                      {t("careersPage.application.form.submit")}
                      <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-10 py-5 sm:py-6 bg-[#00f3ff]/5 border-t border-[#00f3ff]/10 text-center backdrop-blur-md">
          <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em] opacity-60">
            {t("common.contact.footer") || "AHV Holding • Secure & Encrypted"}
          </p>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
};

export default ApplicationModal;
